/**
 * slipQueue — pure queue logic behind the C5 slip-toasts.
 *
 * Slips are loose paper notes that drift onto the painting's lower corner.
 * At most `MAX_VISIBLE_SLIPS` are on screen; a new arrival retires the oldest.
 * The DOM side (SlipToasts.ts) owns elements and timers; this module owns the
 * ordering rules so they can be tested without a browser.
 */

/** Doctrine: the painting keeps its corner; never more than three slips. */
export const MAX_VISIBLE_SLIPS = 3;

/** Default dwell before a slip fades, in ms. */
export const SLIP_HOLD_MS = 5200;

export interface SlipEntry {
  id: number;
  text: string;
  /** Timestamp (ms) the slip arrived — used for oldest-first retirement. */
  at: number;
}

export interface PushResult {
  /** The queue after the push, oldest first. */
  queue: SlipEntry[];
  /** Slips that were retired to make room (callers fade these out). */
  retired: SlipEntry[];
}

/**
 * Append a slip, retiring oldest entries beyond `max`.
 * Never mutates the input queue.
 */
export function pushSlip(
  queue: readonly SlipEntry[],
  slip: SlipEntry,
  max: number = MAX_VISIBLE_SLIPS,
): PushResult {
  const limit = Math.max(1, Math.floor(max));
  const next = [...queue, slip];
  const overflow = Math.max(0, next.length - limit);
  return {
    queue: next.slice(overflow),
    retired: next.slice(0, overflow),
  };
}

/** Remove a slip by id. Never mutates the input queue. */
export function removeSlip(queue: readonly SlipEntry[], id: number): SlipEntry[] {
  return queue.filter((s) => s.id !== id);
}

/** Ids of slips whose dwell has elapsed at `now`. */
export function expiredSlipIds(
  queue: readonly SlipEntry[],
  now: number,
  holdMs: number = SLIP_HOLD_MS,
): number[] {
  return queue.filter((s) => now - s.at >= holdMs).map((s) => s.id);
}
